<script setup lang="ts">
import { computed, onBeforeUnmount, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from '@/components/Dialog.vue'
import Button from '@/components/Button.vue'
import { useDisplay } from 'vuetify'

const { mdAndDown } = useDisplay()
const isMobile = computed(() => mdAndDown.value)

type Mode = 'enable' | 'disable'

const props = defineProps<{
  modelValue: boolean
  mode: Mode
  loading?: boolean
  activeLinksCount?: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm'): void
  (e: 'go-to-contacts'): void
}>()

const { t } = useI18n()

const isOpen = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

function close() {
  isOpen.value = false
}

const isBlockedDisable = computed(() => {
  return props.mode === 'disable' && (props.activeLinksCount ?? 0) > 0
})

const blockedUi = computed(() => ({
  title: t('eveLink.dialog.disableBlocked.title'),
  headline: t('eveLink.dialog.disableBlocked.headline'),
  whyTitle: t('eveLink.dialog.disableBlocked.whyTitle'),
  whyText: t('eveLink.dialog.disableBlocked.whyText'),
  howTitle: t('eveLink.dialog.disableBlocked.howTitle'),
  howText: t('eveLink.dialog.disableBlocked.howText'),
  goToContacts: t('eveLink.dialog.disableBlocked.goToContacts'),
  understood: t('eveLink.dialog.disableBlocked.understood'),
}))

const normalUi = computed(() => {
  const isEnable = props.mode === 'enable'

  return {
    title: t(
      isEnable ? 'eveLink.dialog.enable.title' : 'eveLink.dialog.disable.title'
    ),
    description: t(
      isEnable
        ? 'eveLink.dialog.enable.description'
        : 'eveLink.dialog.disable.description'
    ),
    note: !isEnable ? t('eveLink.dialog.disable.note') : '',
    cancelLabel: t('general.cancel'),
    confirmLabel: t(
      isEnable
        ? 'eveLink.dialog.enable.confirm'
        : 'eveLink.dialog.disable.confirm'
    ),
    confirmBtnClass: 'primaryFlat',
  }
})

const openedOnce = ref(false)
watchOpen()

function watchOpen() {
  const stop = setInterval(() => {
    if (isOpen.value && !openedOnce.value) {
      openedOnce.value = true
      nextTick(() => {})
    }
    if (!isOpen.value) openedOnce.value = false
  }, 150)

  onBeforeUnmount(() => clearInterval(stop))
}
</script>

<template>
  <Dialog
    v-model="isOpen"
    :title="isBlockedDisable ? blockedUi.title : normalUi.title"
    max-width="652px"
  >
    <div
      class="eve-link-dialog"
      :class="{ 'eve-link-dialog--mobile': isMobile }"
    >
      <!-- ====== BLOQUEADO ====== -->
      <template v-if="isBlockedDisable">
        <p class="eve-blocked__headline mb-5 mt-1">
          {{ blockedUi.headline }}
        </p>

        <div class="eve-blocked__section mb-5">
          <h4 class="eve-blocked__title">
            {{ blockedUi.whyTitle }}
          </h4>
          <p class="eve-blocked__text">
            {{ blockedUi.whyText }}
          </p>
        </div>

        <div class="eve-blocked__section">
          <h4 class="eve-blocked__title">
            {{ blockedUi.howTitle }}
          </h4>
          <p class="eve-blocked__text">
            {{ blockedUi.howText }}
          </p>
        </div>

        <div
          class="eve-link-dialog__actions mt-10"
          :class="{ 'eve-link-dialog__actions--mobile': isMobile }"
        >
          <Button
            :class="[!isMobile ? 'tertiaryFlat' : 'primaryOutline']"
            :disabled="loading"
            @click="$emit('go-to-contacts')"
          >
            {{ blockedUi.goToContacts }}
          </Button>

          <Button class="primaryFlat" :disabled="loading" @click="close">
            {{ blockedUi.understood }}
          </Button>
        </div>
      </template>

      <!-- ====== NORMAL ====== -->
      <template v-else>
        <p class="subTitle">
          {{ normalUi.description }}
        </p>

        <p v-if="mode === 'disable'" class="messageDisable">
          {{ normalUi.note }}
        </p>

        <h4 class="benefits_label">
          {{ $t('eveLink.dialog.benefits.title') }}
        </h4>

        <div class="d-flex flex-column ga-4">
          <div class="d-flex ga-3 align-start">
            <v-icon
              icon="mdi-account-multiple-outline"
              class="text-green"
              size="22"
            />
            <div>
              <span class="headLine-benefits font-weight-bold">
                {{ $t('eveLink.dialog.benefits.relationship.title') }}
              </span>
              <span class="headLine-benefits">
                : {{ $t('eveLink.dialog.benefits.relationship.desc') }}
              </span>
            </div>
          </div>

          <div class="d-flex ga-3 align-start">
            <v-icon icon="mdi-autorenew" class="text-green" size="22" />
            <div>
              <span class="headLine-benefits font-weight-bold">
                {{ $t('eveLink.dialog.benefits.updatedData.title') }}
              </span>
              <span class="headLine-benefits">
                : {{ $t('eveLink.dialog.benefits.updatedData.desc') }}
              </span>
            </div>
          </div>

          <div class="d-flex ga-3 align-start">
            <v-icon icon="mdi-security" class="text-green" size="22" />
            <div>
              <span class="headLine-benefits font-weight-bold">
                {{ $t('eveLink.dialog.benefits.lgpd.title') }}
              </span>
              <span class="headLine-benefits">
                : {{ $t('eveLink.dialog.benefits.lgpd.desc') }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="eve-link-dialog__actions mt-10"
          :class="{ 'eve-link-dialog__actions--mobile': isMobile }"
        >
          <Button
            :class="[!isMobile ? 'tertiaryFlat' : 'primaryOutline']"
            :disabled="loading"
            @click="close"
          >
            {{ normalUi.cancelLabel }}
          </Button>

          <Button
            :class="normalUi.confirmBtnClass"
            :disabled="loading"
            @click="$emit('confirm')"
          >
            {{ normalUi.confirmLabel }}
          </Button>
        </div>
      </template>
    </div>
  </Dialog>
</template>

<style scoped>
.eve-link-dialog {
  padding-bottom: 24px;
}

.eve-link-dialog--mobile {
  padding: 16px;
}

/* Actions desktop */
.eve-link-dialog__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
}

/* Actions mobile */
.eve-link-dialog__actions--mobile {
  flex-direction: column;
  align-items: stretch;
}

.eve-link-dialog__actions--mobile :deep(button),
.eve-link-dialog__actions--mobile :deep(.v-btn) {
  width: 100%;
}

.eve-blocked__headline {
  color: var(--standart-theme---EVE-error, #ff5252);

  /* subtitle-1/subtitle-1, medium */
  font-family: var(--fontFamilies-roboto, Roboto);
  font-size: var(--fontSize-42, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--lineHeights-42, 28px); /* 175% */
  letter-spacing: 0.024px;
}

.eve-blocked__section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.eve-blocked__title {
  color: var(
    --standart-theme---EVE-opacity-text--secondary,
    rgba(0, 0, 0, 0.6)
  );
  font-family: 'Open Sans';
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 700;
  line-height: var(--lineHeights-49, 22px); /* 157.143% */
  letter-spacing: 0.014px;
}

.eve-blocked__text {
  color: var(
    --standart-theme---EVE-opacity-text--secondary,
    rgba(0, 0, 0, 0.6)
  );
  font-family: 'Open Sans';
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-49, 22px);
  letter-spacing: 0.014px;
}

.subTitle {
  color: var(--standart-theme---EVE-opacity-text--primary, rgba(0, 0, 0, 0.87));

  /* subtitle-1/subtitle-1, medium */
  font-family: var(--fontFamilies-roboto, Roboto);
  font-size: var(--fontSize-42, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--lineHeights-42, 28px); /* 175% */
  letter-spacing: 0.024px;
  margin-bottom: 10px;
}

.messageDisable {
  color: var(
    --standart-theme---EVE-opacity-text--secondary,
    rgba(0, 0, 0, 0.6)
  );
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-49, 22px); /* 157.143% */
  letter-spacing: 0.014px;
  margin-bottom: 10px;
}

.benefits_label {
  color: var(--standart-theme---EVE-opacity-text--primary, rgba(0, 0, 0, 0.87));

  /* subtitle-1/subtitle-1, medium */
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-42, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--lineHeights-42, 28px); /* 175% */
  letter-spacing: 0.024px;
  margin-bottom: 10px;
}

.headLine-benefits {
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
  font-family: 'Open Sans';
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-49, 22px);
  letter-spacing: 0.014px;
}

.text-benefits {
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
  font-family: 'Open Sans';
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-49, 22px);
  letter-spacing: 0.014px;
}
</style>
